import { Switch } from "@/components/shadcn/switch";
import { Skeleton } from "@/components/shadcn/skeleton";
import { BotMessageSquare, Edit, ExternalLink, Trash } from "lucide-react";
import { AddNewEntry, Tool } from "./AssistantDetailsContent";
import { useMemo, useState } from "react";
import { NoxusMenu } from "../UI/NoxusMenu";
import { NoxusTooltip } from "../UI/NoxusTooltip";
import {
  AgentToolSettingsInput,
  AssistantWithTags,
} from "@/generated/types.gen";
import { AssistantsService } from "@/generated";
import { AgentPickerModal } from "./AgentPickerModal";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useQuery } from "@tanstack/react-query";
import { useSpotUser } from "@/utils/spotUser";
import { NoxusAlert } from "../UI/NoxusAlert";
import { NoxusIcon } from "../UI/NoxusIcon";
import { NoxusFormEntryConfig } from "@/components/Spotflow/ConfigDrawer/ConfigDrawer";
import { RichTextValue } from "@/components/Form/RichTextField";
import { useDisclosure } from "@/utils/useDisclosure";
import { ExpandableTextarea } from "./ExpandableTextarea";
import { useParams, useRouter } from "next/navigation";
import { UnavailableResourceBadge } from "./UnavailableResourceBadge";

export const AgentsField = ({
  tools,
  onToolsChange,
  isNodeConfig,
  isDisabled,
  itemConfig,
}: {
  tools: Tool[] | undefined;
  onToolsChange: (tools: Tool[]) => void;
  isNodeConfig?: boolean;
  isDisabled?: boolean;
  itemConfig?: Record<string, NoxusFormEntryConfig>;
}) => {
  const { spotGroup } = useSpotUser();
  const router = useRouter();
  const params = useParams();
  const currentAssistantId = useMemo(
    () => (params?.assistantId as string | undefined) ?? undefined,
    [params]
  );
  const { isOpen, onOpen, onClose } = useDisclosure();
  const agentsTools = useMemo(() => {
    return tools?.filter((tool) => tool.type === "agent_tool");
  }, [tools]) as AgentToolSettingsInput[] | undefined;

  const agentIds = useMemo(() => {
    return agentsTools?.map((t) => t.agent_id).join(",") || "";
  }, [agentsTools]);

  const {
    data: agentsData,
    isLoading: isLoadingAgents,
    isSuccess: agentsLoaded,
  } = useRemoteData(
    DATA_KEYS.ASSISTANTS,
    {
      path: { group_id: spotGroup?.id },
      query: { ids: agentIds, include_deleted: true },
    },
    {
      staleTime: Infinity,
      enabled: !!agentIds,
    }
  );

  const ignoreAgentIds = useMemo(() => {
    const ids = [...(agentsTools?.map((t) => t.agent_id) ?? [])];
    if (currentAssistantId) ids.push(currentAssistantId);
    return ids.join(",");
  }, [agentsTools, currentAssistantId]);

  const { data: availabilityData } = useQuery({
    queryKey: [DATA_KEYS.ASSISTANTS, "availability", ignoreAgentIds],
    queryFn: () =>
      AssistantsService.getAssistants({
        path: { group_id: spotGroup.id },
        query: {
          size: 1,
          page: 1,
          ignore_ids: ignoreAgentIds || undefined,
        },
      }),
    staleTime: 30_000,
    enabled: !!spotGroup?.id,
  });
  const remainingAgentsCount = availabilityData?.data?.total ?? null;

  const onChange = (tool?: Tool, enabled?: boolean) => {
    if (isDisabled) return;
    if (!tool) {
      return;
    }
    const newTools = tools?.map((t) => {
      if (
        (t as AgentToolSettingsInput).agent_id ===
        (tool as AgentToolSettingsInput).agent_id
      ) {
        return { ...t, enabled };
      }
      return t;
    });

    if (newTools) {
      onToolsChange(newTools);
    }
  };

  const onAddAgent = (props: {
    agent_id: string;
    version_id: string | null;
    config: Record<string, unknown>;
  }) => {
    const { agent_id, version_id, config } = props;
    if (isDisabled) return;
    if (editingTool) {
      onToolsChange(
        tools?.map((t) => {
          if ((t as AgentToolSettingsInput).agent_id === editingTool.agent_id) {
            return {
              ...t,
              ...config,
              version_id,
            };
          }
          return t;
        }) || []
      );
    } else {
      onToolsChange([
        ...(tools || []),
        {
          type: "agent_tool",
          enabled: true,
          agent_id,
          version_id,
          ...config,
        } as AgentToolSettingsInput,
      ]);
    }
  };

  const activeAgents = useMemo(() => {
    return (
      agentsTools?.map((tool) => ({
        id: tool.agent_id,
        name: tool.agent_id,
      })) || []
    );
  }, [agentsTools]);

  const [editingTool, setEditingTool] = useState<
    AgentToolSettingsInput | undefined
  >(undefined);

  const onEdit = (tool: AgentToolSettingsInput) => {
    if (isDisabled) return;
    setEditingTool(tool);
    onOpen();
  };

  const onDelete = (tool: AgentToolSettingsInput) => {
    if (isDisabled) return;
    if (tools) {
      onToolsChange(
        tools.filter(
          (t) => (t as AgentToolSettingsInput).agent_id !== tool.agent_id
        )
      );
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div>
          <p className="text-sm font-medium text-foreground leading-none">
            Agents
          </p>
          <p className="text-xs font-regular text-muted-foreground mt-1.5">
            Select the agents this one can collaborate with.
          </p>
        </div>

        {agentsTools?.map((tool) => {
          const agentData = agentsData?.items?.find(
            (a) => a.id === tool.agent_id
          );
          const isMissing =
            agentsLoaded && (!agentData || !!agentData.deleted_at);
          return (
            <AgentEntry
              key={tool.agent_id}
              tool={tool}
              agentData={agentData}
              isMissing={isMissing}
              isLoadingData={isLoadingAgents}
              onChange={(enabled) => {
                onChange(tool, enabled);
              }}
              onDelete={() => {
                onDelete(tool);
              }}
              onEdit={() => {
                onEdit(tool);
              }}
              onExtraInstructionsChange={(value) => {
                if (isDisabled) return;
                const newTools = tools?.map((t) => {
                  if (
                    (t as AgentToolSettingsInput).agent_id === tool.agent_id
                  ) {
                    return { ...t, extra_instructions: value };
                  }
                  return t;
                });
                if (newTools) {
                  onToolsChange(newTools as Tool[]);
                }
              }}
              isDisabled={isDisabled}
              fieldConfig={itemConfig?.["extra_instructions"]}
            />
          );
        })}

        {(agentsTools?.length ?? 0) === 0 && remainingAgentsCount === 0 ? (
          <NoxusAlert
            icon={BotMessageSquare}
            description="No agents available."
            action={{
              label: (
                <>
                  <ExternalLink />
                  Go to Workshop
                </>
              ),
              onClick: () => router.push("/?tab=agents"),
            }}
          />
        ) : (
          <AddNewEntry
            label="Add Agent"
            disabledLabel={
              remainingAgentsCount === 0 ? "No more agents to add" : undefined
            }
            onClick={onOpen}
            isDisabled={isDisabled || remainingAgentsCount === 0}
          />
        )}
      </div>
      {isOpen && (
        <AgentPickerModal
          isOpen={isOpen}
          onClose={() => {
            setEditingTool(undefined);
            onClose();
          }}
          onSelectAgent={onAddAgent}
          openAgents={activeAgents}
          editingTool={editingTool}
          itemConfig={itemConfig}
        />
      )}
    </div>
  );
};

const AgentEntry = ({
  tool,
  agentData,
  isMissing,
  isLoadingData,
  onChange,
  onDelete,
  onEdit,
  onExtraInstructionsChange,
  isDisabled,
  fieldConfig,
}: {
  tool: AgentToolSettingsInput;
  agentData?: AssistantWithTags;
  isMissing?: boolean;
  isLoadingData?: boolean;
  onChange: (enabled: boolean) => void;
  onDelete: () => void;
  onEdit: () => void;
  onExtraInstructionsChange: (value: RichTextValue) => void;
  isDisabled?: boolean;
  fieldConfig?: NoxusFormEntryConfig;
}) => {
  const { name } = agentData || {};
  const router = useRouter();

  return (
    <div className="flex flex-col border border-border rounded-lg w-full overflow-hidden">
      <div className="flex items-center justify-between gap-2 p-2">
        <div className="flex items-center gap-2 flex-1">
          <NoxusIcon icon={agentData?.image_url || "coworker"} />
          <div className="flex-1 flex items-center gap-2 min-w-0">
            {isLoadingData ? (
              <Skeleton className="h-5 w-32 mb-1" />
            ) : (
              <>
                <NoxusTooltip content="Open in new tab" placement="top" asChild>
                  <a
                    href={`/assistant/${tool.agent_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    <p className="font-sm text-sm">{name || "Deleted agent"}</p>
                  </a>
                </NoxusTooltip>
                {isMissing && <UnavailableResourceBadge kind="agent" />}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={tool.enabled}
            onCheckedChange={(checked) => onChange(checked)}
            disabled={isDisabled}
          />
          <NoxusMenu
            isSmall
            itemsArray={[
              {
                label: "Edit",
                icon: Edit,
                onClick: onEdit,
                isHidden: isMissing,
              },
              {
                label: "Open in new tab",
                icon: ExternalLink,
                onClick: () => {
                  router.push(`/assistant/${tool.agent_id}`);
                },
              },
              {
                label: "Remove",
                icon: Trash,
                onClick: onDelete,
                color: "destructive",
              },
            ]}
            isDisabled={isDisabled}
          />
        </div>
      </div>

      {tool.enabled && !isMissing && (
        <ExpandableTextarea
          value={tool.extra_instructions || ""}
          onChange={(value) => onExtraInstructionsChange(value)}
          isDisabled={isDisabled}
          label="Agent usage conditions"
          placeholder="Specify when and how this agent should be used..."
          fieldConfig={fieldConfig}
        />
      )}
    </div>
  );
};
